import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Gem } from 'lucide-react';
import { CONSTANTS } from './data.js';
import { ASSETS } from './assetManifest.js';

// --- HELPER FUNCTIONS & DATA ---

const getNumberColor = (n: number) => CONSTANTS.NUMBER_COLORS[n];

const getBetType = (n: number) => {
  if (n === 0) return [];
  const types = [];
  if (n % 2 === 0) types.push('even'); else types.push('odd');
  if (getNumberColor(n) === 'red') types.push('red'); else types.push('black');
  if (n >= 1 && n <= 18) types.push('low'); else types.push('high');
  if (n >= 1 && n <= 12) types.push('dozen1');
  else if (n >= 13 && n <= 24) types.push('dozen2');
  else types.push('dozen3');
  if (n % 3 === 1) types.push('col3');
  else if (n % 3 === 2) types.push('col2');
  else types.push('col1');
  return types;
};

// --- UI COMPONENTS (within App.tsx for simplicity) ---

const Header = ({ isConnected, account, balance, onConnect }) => (
  <header className="w-full p-4 bg-black bg-opacity-50 flex justify-between items-center text-white shadow-lg">
    <h1 className="text-2xl font-bold text-yellow-400 tracking-wider" style={{ fontFamily: 'serif' }}>
      Roulette Royale
    </h1>
    <div className="flex items-center space-x-4">
      {isConnected && (
        <div className="flex items-center space-x-2 bg-gray-800 px-3 py-1 rounded-full">
          <Gem className="text-blue-400" size={20} />
          <span className="font-semibold text-lg">{balance.toLocaleString()} $CHIPS</span>
        </div>
      )}
      <button
        onClick={onConnect}
        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-4 rounded-full flex items-center transition-colors"
      >
        <Wallet size={20} className="mr-2" />
        {isConnected ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Connect Wallet'}
      </button>
    </div>
  </header>
);

// --- Enhanced RouletteWheel with Realistic Visuals ---
const RouletteWheel = ({ isSpinning, winningNumber }) => {
  const wheelOrder = CONSTANTS.WHEEL_ORDER;
  const anglePerSegment = 360 / wheelOrder.length;

  // Ball animation state
  const [ballAngle, setBallAngle] = useState(0);
  const [ballAnimating, setBallAnimating] = useState(false);

  // Calculate the angle for the winning number
  const getWinningAngle = (number) => {
    if (number === null) return 0;
    const index = wheelOrder.indexOf(number);
    // Ball spins clockwise, so angle increases
    return index * anglePerSegment;
  };

  // Ball animation effect with enhanced physics
  useEffect(() => {
    let animationFrame;
    let startTime;

    if (isSpinning) {
      setBallAnimating(true);
      const totalSpins = CONSTANTS.BALL_ANIMATION_TOTAL_SPINS;
      const duration = CONSTANTS.BALL_ANIMATION_DURATION;
      
      // Enhanced easing function for more realistic ball physics
      const easing = (t) => {
        // Combine ease-out-cubic with a slight bounce at the end
        const easeOut = 1 - Math.pow(1 - t, 3);
        const bounce = t > 0.9 ? Math.sin((t - 0.9) * 50) * 0.02 * (1 - t) : 0;
        return easeOut + bounce;
      };

      const startAngle = 0;
      const endAngle =
        totalSpins * 360 +
        getWinningAngle(winningNumber === null ? 0 : winningNumber);

      function animateBall(ts) {
        if (!startTime) startTime = ts;
        const elapsed = ts - startTime;
        const t = Math.min(elapsed / duration, 1);
        const easedT = easing(t);
        const currentAngle =
          startAngle + (endAngle - startAngle) * easedT;
        setBallAngle(currentAngle % 360);

        if (t < 1) {
          animationFrame = requestAnimationFrame(animateBall);
        } else {
          setBallAngle(getWinningAngle(winningNumber === null ? 0 : winningNumber));
          setBallAnimating(false);
        }
      }

      animationFrame = requestAnimationFrame(animateBall);
    } else {
      // When not spinning, set ball to winning number or 0
      setBallAngle(getWinningAngle(winningNumber === null ? 0 : winningNumber));
      setBallAnimating(false);
    }

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [isSpinning, winningNumber]);

  // Responsive wheel size
  const wheelSize = window.innerWidth >= 768 ? CONSTANTS.WHEEL_SIZE_DESKTOP : CONSTANTS.WHEEL_SIZE_MOBILE;
  const ballRadius = (wheelSize / 2) * CONSTANTS.BALL_TRACK_RADIUS_RATIO;

  // Ball position calculation with enhanced track positioning
  const ballCenter = {
    x: wheelSize / 2 + ballRadius * Math.cos((ballAngle - 90) * Math.PI / 180),
    y: wheelSize / 2 + ballRadius * Math.sin((ballAngle - 90) * Math.PI / 180),
  };

  // Enhanced wheel styling
  const wheelStyle = {
    filter: `
      drop-shadow(0 ${CONSTANTS.WHEEL_SHADOW_BLUR}px ${CONSTANTS.WHEEL_SHADOW_BLUR * 2}px rgba(0, 0, 0, 0.5))
      drop-shadow(0 0 ${CONSTANTS.WHEEL_SHADOW_SPREAD * 2}px ${CONSTANTS.CASINO_LIGHTING_COLOR}40)
    `,
    borderRadius: '50%',
    border: `4px solid ${CONSTANTS.WHEEL_BORDER_COLOR}`,
    background: `radial-gradient(circle at 30% 30%, rgba(255, 215, 0, 0.1), transparent 50%)`,
  };

  // Enhanced ball styling
  const ballStyle = {
    width: CONSTANTS.BALL_IMAGE_SIZE + 'px',
    height: CONSTANTS.BALL_IMAGE_SIZE + 'px',
    left: `${ballCenter.x - CONSTANTS.BALL_IMAGE_SIZE / 2}px`,
    top: `${ballCenter.y - CONSTANTS.BALL_IMAGE_SIZE / 2}px`,
    transition: ballAnimating ? 'none' : 'left 0.5s ease-out, top 0.5s ease-out',
    zIndex: 10,
    pointerEvents: 'none',
    filter: `
      drop-shadow(0 ${CONSTANTS.BALL_SHADOW_BLUR}px ${CONSTANTS.BALL_SHADOW_BLUR}px rgba(0, 0, 0, 0.6))
      drop-shadow(0 0 ${CONSTANTS.BALL_GLOW_SIZE}px rgba(255, 255, 255, 0.8))
    `,
    transform: ballAnimating ? 'scale(1.1)' : 'scale(1)',
  };

  return (
    <div className="relative flex items-center justify-center">
      {/* Casino lighting effect */}
      <div 
        className="absolute rounded-full"
        style={{
          width: wheelSize + 100,
          height: wheelSize + 100,
          background: `radial-gradient(circle, ${CONSTANTS.CASINO_LIGHTING_COLOR}10 0%, transparent 70%)`,
          zIndex: -1,
        }}
      />
      
      {/* Wheel container with enhanced styling */}
      <div 
        className="relative"
        style={{
          width: wheelSize,
          height: wheelSize,
        }}
      >
        {/* Enhanced wheel with realistic effects */}
        <img
          src={ASSETS.game.rouletteWheel.path}
          alt="Roulette Wheel"
          className="w-full h-full"
          style={wheelStyle}
          draggable={false}
        />
        
        {/* Ball track indicator (subtle) */}
        <div
          className="absolute border border-yellow-400 rounded-full opacity-20"
          style={{
            width: ballRadius * 2,
            height: ballRadius * 2,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            borderWidth: '1px',
            borderStyle: 'dashed',
          }}
        />
        
        {/* Enhanced ball with realistic physics */}
        <img
          src={ASSETS.game.rouletteBall.path}
          alt="Roulette Ball"
          className="absolute"
          style={ballStyle}
          draggable={false}
        />
        
        {/* Ball trail effect when spinning */}
        {ballAnimating && (
          <div
            className="absolute rounded-full opacity-30"
            style={{
              width: CONSTANTS.BALL_IMAGE_SIZE / 2,
              height: CONSTANTS.BALL_IMAGE_SIZE / 2,
              left: `${ballCenter.x - CONSTANTS.BALL_IMAGE_SIZE / 4}px`,
              top: `${ballCenter.y - CONSTANTS.BALL_IMAGE_SIZE / 4}px`,
              background: 'radial-gradient(circle, white, transparent)',
              zIndex: 9,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
      
      {/* Reflection effect on table */}
      <div 
        className="absolute opacity-20"
        style={{
          width: wheelSize * 0.8,
          height: wheelSize * 0.3,
          bottom: -wheelSize * 0.15,
          background: `radial-gradient(ellipse, rgba(255, 215, 0, 0.3) 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'perspective(100px) rotateX(45deg)',
          zIndex: -1,
        }}
      />
    </div>
  );
};

const BettingTable = ({ bets, onBet, selectedChip }) => {
  const numberGrid = Array.from({ length: 12 }, (_, i) => [34 - i * 3, 35 - i * 3, 36 - i * 3]);

  const BetButton = ({ label, onClick, className = '', betKey }) => (
    <button onClick={onClick} className={`relative border border-yellow-600/50 text-yellow-400 font-bold flex items-center justify-center transition-colors hover:bg-yellow-400/20 ${className}`}>
      {label}
      {bets[betKey] > 0 && (
        <div className="absolute w-8 h-8 bg-blue-500/80 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white">
          {bets[betKey]}
        </div>
      )}
    </button>
  );

  const NumberButton = ({ n }) => {
    const color = getNumberColor(n);
    const colorClass = color === 'red' ? 'bg-red-700' : color === 'black' ? 'bg-black' : 'bg-green-700';
    return <BetButton label={n} onClick={() => onBet(String(n))} className={`h-12 ${colorClass}`} betKey={String(n)} />;
  };

  return (
    <div className="p-4 bg-green-900/80 rounded-lg border-2 border-yellow-700/50 w-full max-w-4xl">
      <div className="grid grid-cols-[1fr_3fr] gap-1">
        <BetButton label="0" onClick={() => onBet('0')} className="h-full row-span-3 bg-green-700" betKey="0" />
        <div className="grid grid-cols-12 gap-1">
          {numberGrid.slice().reverse().flat().map(n => <NumberButton key={n} n={n} />)}
        </div>
      </div>
      <div className="grid grid-cols-6 gap-1 mt-1">
        <BetButton label="1-18" onClick={() => onBet('low')} className="h-12 col-span-2" betKey="low" />
        <BetButton label="EVEN" onClick={() => onBet('even')} className="h-12" betKey="even" />
        <BetButton label="RED" onClick={() => onBet('red')} className="h-12 bg-red-700" betKey="red" />
        <BetButton label="BLACK" onClick={() => onBet('black')} className="h-12 bg-black" betKey="black" />
        <BetButton label="ODD" onClick={() => onBet('odd')} className="h-12" betKey="odd" />
        <BetButton label="19-36" onClick={() => onBet('high')} className="h-12 col-span-2" betKey="high" />
      </div>
      <div className="grid grid-cols-3 gap-1 mt-1">
        <BetButton label="1st 12" onClick={() => onBet('dozen1')} className="h-12" betKey="dozen1" />
        <BetButton label="2nd 12" onClick={() => onBet('dozen2')} className="h-12" betKey="dozen2" />
        <BetButton label="3rd 12" onClick={() => onBet('dozen3')} className="h-12" betKey="dozen3" />
      </div>
    </div>
  );
};

const Controls = ({ selectedChip, onChipSelect, onSpin, onClear, totalBet, isSpinning, message }) => (
  <div className="w-full p-4 bg-black bg-opacity-50 flex flex-col md:flex-row justify-between items-center text-white space-y-4 md:space-y-0">
    <div className="flex items-center space-x-2">
      <span className="font-semibold mr-2">Chip:</span>
      {CONSTANTS.CHIP_VALUES.map(value => (
        <button key={value} onClick={() => onChipSelect(value)} className={`transition-transform transform hover:scale-110 ${selectedChip === value ? 'scale-110' : ''}`}>
          <img src={ASSETS.chips[`chip_${value}`].path} alt={`${value} chip`} className={`w-12 h-12 ${selectedChip !== value ? 'opacity-60' : ''}`} />
        </button>
      ))}
    </div>
    <div className="text-center">
      <AnimatePresence>
        <motion.div
          key={message}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="text-xl font-bold text-yellow-300"
        >
          {message}
        </motion.div>
      </AnimatePresence>
      <div className="text-lg">Total Bet: <span className="font-bold">{totalBet}</span></div>
    </div>
    <div className="flex space-x-2">
      <button onClick={onClear} disabled={isSpinning} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 transition-colors">Clear</button>
      <button onClick={onSpin} disabled={isSpinning || totalBet === 0} className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">SPIN</button>
    </div>
  </div>
);

// --- MAIN APP COMPONENT ---

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [balance, setBalance] = useState(CONSTANTS.INITIAL_CHIP_BALANCE);
  const [bets, setBets] = useState({});
  const [selectedChip, setSelectedChip] = useState(CONSTANTS.CHIP_VALUES[0]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winningNumber, setWinningNumber] = useState<number | null>(null);
  const [message, setMessage] = useState('Place your bets!');

  const totalBet = Object.values(bets).reduce((sum, val) => sum + val, 0);

  const handleConnect = () => {
    // Mock wallet connection
    setIsConnected(!isConnected);
  };

  const handleBet = (betKey: string) => {
    if (isSpinning) return;
    if (balance < selectedChip) {
      setMessage("Not enough chips!");
      return;
    }
    setBalance(prev => prev - selectedChip);
    setBets(prev => ({
      ...prev,
      [betKey]: (prev[betKey] || 0) + selectedChip,
    }));
  };

  const handleClear = () => {
    if (isSpinning) return;
    setBalance(prev => prev + totalBet);
    setBets({});
  };

  const handleSpin = () => {
    if (isSpinning || totalBet === 0) return;
    
    setIsSpinning(true);
    setMessage('No more bets!');
    setWinningNumber(null);

    // Mocking Chainlink VRF call
    setTimeout(() => {
      const resultNumber = Math.floor(Math.random() * 37);
      setWinningNumber(resultNumber);

      // Calculate winnings after spin animation
      setTimeout(() => {
        let winnings = 0;
        const winningBetTypes = getBetType(resultNumber);

        for (const [betKey, betAmount] of Object.entries(bets)) {
          let payoutMultiplier = 0;
          if (betKey === String(resultNumber)) {
            payoutMultiplier = CONSTANTS.BET_PAYOUTS.straight;
          } else if (winningBetTypes.includes(betKey)) {
            if (['red', 'black'].includes(betKey)) payoutMultiplier = CONSTANTS.BET_PAYOUTS.red_black;
            if (['even', 'odd'].includes(betKey)) payoutMultiplier = CONSTANTS.BET_PAYOUTS.odd_even;
            if (['low', 'high'].includes(betKey)) payoutMultiplier = CONSTANTS.BET_PAYOUTS.high_low;
            if (['dozen1', 'dozen2', 'dozen3'].includes(betKey)) payoutMultiplier = CONSTANTS.BET_PAYOUTS.dozen;
            if (['col1', 'col2', 'col3'].includes(betKey)) payoutMultiplier = CONSTANTS.BET_PAYOUTS.column;
          }
          
          if (payoutMultiplier > 0) {
            winnings += betAmount + (betAmount * payoutMultiplier);
          }
        }

        const color = getNumberColor(resultNumber);
        setMessage(`Winner is ${resultNumber} ${color}! You won ${winnings.toLocaleString()}!`);
        setBalance(prev => prev + winnings);
        setBets({});
        setIsSpinning(false);
      }, CONSTANTS.BALL_ANIMATION_DURATION + 500); // Wait for spin to finish + a little extra
    }, 500); // Small delay to start spin
  };

  React.useEffect(() => {
    if (!isSpinning && totalBet === 0) {
      setTimeout(() => setMessage('Place your bets!'), 3000);
    }
  }, [isSpinning, totalBet]);

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center bg-cover bg-center"
      style={{ backgroundImage: `url(${ASSETS.backgrounds.table.path})` }}
    >
      <div className="w-full h-full flex flex-col items-center bg-black/50">
        <Header
          isConnected={isConnected}
          account={CONSTANTS.MOCK_ACCOUNT}
          balance={balance}
          onConnect={handleConnect}
        />
        <main className="flex-grow w-full flex flex-col lg:flex-row items-center justify-center p-4 gap-8">
          <div className="flex-shrink-0">
            <RouletteWheel isSpinning={isSpinning} winningNumber={winningNumber} />
          </div>
          <div className="w-full lg:w-auto">
            <BettingTable bets={bets} onBet={handleBet} selectedChip={selectedChip} />
          </div>
        </main>
        <Controls
          selectedChip={selectedChip}
          onChipSelect={setSelectedChip}
          onSpin={handleSpin}
          onClear={handleClear}
          totalBet={totalBet}
          isSpinning={isSpinning}
          message={message}
        />
      </div>
    </div>
  );
}

export default App;
